import type { AnalyticsExportFormat, AnalyticsExportSamplePolicy } from '@/lib/analytics-export'

export type AnalyticsExportRequestRangeId = '7d' | '30d' | '90d' | 'custom'
export type AnalyticsExportRequestScopeId = 'all' | 'team' | 'own'
export type AnalyticsExportRequestStatus = 'complete'
export type AnalyticsExportRequestCustomerWorkflow =
  | 'all'
  | 'new_customer'
  | 'existing_customer_change'

export type AnalyticsExportRequestInput = {
  format: AnalyticsExportFormat
  range: {
    id: AnalyticsExportRequestRangeId
    label: string
  }
  scope: {
    id: AnalyticsExportRequestScopeId
    label: string
  }
  status: {
    id: AnalyticsExportRequestStatus
    label: string
  }
  customerWorkflow: {
    id: AnalyticsExportRequestCustomerWorkflow
    label: string
  }
  redactionPolicy: readonly AnalyticsExportSamplePolicy[]
}

export type AnalyticsExportRequestPayload = {
  format: AnalyticsExportFormat
  range: AnalyticsExportRequestRangeId
  scope: AnalyticsExportRequestScopeId
  status: AnalyticsExportRequestStatus
  customerWorkflow: AnalyticsExportRequestCustomerWorkflow
  redactionPolicy: readonly AnalyticsExportSamplePolicy[]
}

export type AnalyticsExportRequestRow = {
  field: string
  value: string
}

export type AnalyticsExportRequest = {
  payload: AnalyticsExportRequestPayload
  rows: AnalyticsExportRequestRow[]
  mockSafe: true
}

const forbiddenRequestKeys = new Set([
  'answer',
  'answers',
  'rawanswer',
  'rawanswers',
  'sample',
  'samples',
  'rawsample',
  'rawsamples',
  'email',
  'contactemail',
  'phone',
  'contactphone',
  'mobile',
  'address',
  'companyaddress',
  'taxid',
  'tax_id',
  'companytaxid',
  'businessaccountingno',
  'providerpayload',
  'd1rows',
  'rawd1rows',
])

const unsafeRequestValuePatterns = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /09\d{8}/,
  /0\d{1,2}-?\d{6,8}(?:#?\d+)?/,
  /[\u4e00-\u9fff]{0,8}(市|縣).{0,24}(路|街|巷|弄|號)/,
  /(^|[^\d])\d{8}([^\d]|$)/,
]

export function buildAnalyticsExportRequest(
  input: AnalyticsExportRequestInput,
): AnalyticsExportRequest {
  assertSafeAnalyticsExportRequest(input)

  const payload: AnalyticsExportRequestPayload = {
    format: input.format,
    range: input.range.id,
    scope: input.scope.id,
    status: input.status.id,
    customerWorkflow: input.customerWorkflow.id,
    redactionPolicy: input.redactionPolicy,
  }

  return {
    mockSafe: true,
    payload,
    rows: [
      { field: 'format', value: `${input.format}（匯出前預覽基準）` },
      { field: 'range', value: `${input.range.id} / ${input.range.label}` },
      { field: 'scope', value: input.scope.label },
      { field: 'status', value: input.status.label },
      { field: 'customerWorkflow', value: input.customerWorkflow.label },
      { field: 'redactionPolicy', value: input.redactionPolicy.join(' + ') },
      {
        field: 'mock-safe 說明',
        value: 'sanitized payload preview，不查 D1，不寫入匯出檔',
      },
    ],
  }
}

function assertSafeAnalyticsExportRequest(value: unknown, path = 'input') {
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertSafeAnalyticsExportRequest(item, `${path}[${index}]`))
    return
  }

  if (value && typeof value === 'object') {
    for (const [key, nestedValue] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase()
      if (forbiddenRequestKeys.has(normalizedKey)) {
        throw new Error(`Unsafe analytics export request: forbidden key ${path}.${key}`)
      }
      assertSafeAnalyticsExportRequest(nestedValue, `${path}.${key}`)
    }
    return
  }

  if (typeof value !== 'string') return

  if (unsafeRequestValuePatterns.some((pattern) => pattern.test(value))) {
    throw new Error(`Unsafe analytics export request: forbidden value at ${path}`)
  }
}
